// Social auth providers shown across both apps. Single source of truth so the web
// and RN sign-in screens render the same set, order, and labels. Provider ids are
// the exact keys Supabase expects (`azure` = Microsoft). Brand styling lives in
// each app's button component — this stays presentation-agnostic.
//
// Facebook (`facebook`) is intentionally omitted for now — Meta requires an aged
// account + business verification we don't have yet. To add it later: enable the
// provider in Supabase, then add `{ id: "facebook", label: "Continue with
// Facebook", connectLabel: "Facebook" }` here and a Facebook icon in the apps'
// ProviderIcon — everything else is data-driven.

// Minimum password length enforced client-side across every set/change/reset flow
// in both apps. Single source of truth so the rule (and its message, via the
// interpolated i18n key) can't drift between screens. Keep in sync with the
// Supabase project's own password-length setting.
export const MIN_PASSWORD_LENGTH = 8;

export type AuthProviderId = "google" | "apple" | "azure";

export interface AuthProviderMeta {
  /** Supabase provider key. */
  id: AuthProviderId;
  /** Full sign-in button label, e.g. "Continue with Google". */
  label: string;
  /** Short name for "Connect X" / "X connected" in settings. */
  connectLabel: string;
}

export const AUTH_PROVIDERS: readonly AuthProviderMeta[] = [
  { id: "google", label: "Continue with Google", connectLabel: "Google" },
  { id: "apple", label: "Continue with Apple", connectLabel: "Apple" },
  { id: "azure", label: "Continue with Microsoft", connectLabel: "Microsoft" },
] as const;

export interface AuthIdentityLike {
  provider: string;
}

// True when the user can sign in with email + password (has an `email` identity).
// Drives whether "Change password" management is relevant vs. offering to "Set a
// password" for a social-only account.
export function hasPasswordIdentity(identities?: AuthIdentityLike[] | null): boolean {
  return !!identities?.some((i) => i.provider === "email");
}

// The social providers currently linked to the account (excludes `email`).
export function connectedProviders(
  identities?: AuthIdentityLike[] | null,
): AuthProviderId[] {
  const ids = new Set(AUTH_PROVIDERS.map((p) => p.id as string));
  return (identities ?? [])
    .map((i) => i.provider)
    .filter((p): p is AuthProviderId => ids.has(p));
}
